import { ChangeEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Dialog,
  FormControlLabel,
  ModalProps,
  Switch,
  Typography,
} from '@mui/material';
import { Grid } from '@mui/system';
import { SsoService, TenantEmailDiscoveryResponse, TotpService } from 'api';
import { firmsAction } from 'common/actions';
import { CANNOT_UPDATE_2FA, PAGE_TITLE, REAUTHENTICATION_MESSAGE, TWO_FA_ORG_MANDATORY_HINT } from 'common/constants/auth';
import { useStore } from 'common/store';
import { LayoutContextValues, PageBreadcrumb, UseStoreValues } from 'common/types/store';
import { LayoutContext } from 'context';
import { BackupCodes, OTP as Otp, SignOutButton } from 'pages/Auth/components';
import type { DeviceData } from 'pages/Auth/components/OTP';
import { useResponse } from 'services/hooks';
import { Logger } from 'utilities';
import { User2FAStatus } from './User2FA.props';
import { StyledFormGroup, StyledHeading, StyledMessage, StyledMessageContainer } from './User2FA.styles';

const User2FA = () => {
  const [user2faStatus, setUser2faStatus] = useState<User2FAStatus>({
    user_has_totp_device: false,
    confirmed: false,
    persistent_id: '',
  });
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [showOTP, setShowOTP] = useState(false);
  const [isGeneratingBackupCodes, setIsGeneratingBackupCodes] = useState(false);
  const [displayReauthenticate, setDisplayReauthenticate] = useState(false);
  const [isTwoFactorMandatory, setIsTwoFactorMandatory] = useState(false);
  const [{ user }] = useStore() as unknown as UseStoreValues;

  const { processErrorResponse } = useResponse();
  const { setPageTitle, setPageBreadcrumbs } = useContext(LayoutContext) as unknown as LayoutContextValues;

  const handleDialogClose: ModalProps['onClose'] = (_event, reason) => {
    if (reason && reason === 'backdropClick') {
      setShowOTP(false);
    }
  };

  const handle2faChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    const isDisablingMandatoryTwoFactor = isTwoFactorMandatory && !checked;

    if (isDisablingMandatoryTwoFactor) {
      return;
    }

    // Launch the OTP dialog if the user is setting up 2FA for the first time
    if (!user2faStatus.user_has_totp_device && checked) {
      setShowOTP(true);
      return;
    }

    const updateTOTP = async () => {
      try {
        if (!checked) {
          await TotpService.apiTotpDeleteCreate();
          setUser2faStatus({
            user_has_totp_device: false,
            confirmed: false,
          });
        } else {
          setShowOTP(true);
        }
      } catch (err) {
        processErrorResponse({
          error: err,
          defaultErrorMessage: CANNOT_UPDATE_2FA,
        });
        // Revert the switch status to the previous state if there was an error
        setUser2faStatus(prevStatus => ({
          ...prevStatus,
          confirmed: !checked,
        }));
      }
    };

    if (user2faStatus.user_has_totp_device) {
      updateTOTP();
    }
  };

  const generateNewBackupCodes = useCallback(async () => {
    setIsGeneratingBackupCodes(true);

    try {
      const response = await TotpService.apiTotpStaticCreateCreate();
      if (response) {
        setBackupCodes(response.tokens);
      }
    } catch (error: unknown) {
      // If we receive a Forbidden error we need to reauthenticate the user
      if ((error as { status?: number })?.status === 403) {
        setDisplayReauthenticate(true);
      }
      Logger.error('Error generating backup codes', error);
    } finally {
      setIsGeneratingBackupCodes(false);
    }
  }, []);

  const breadcrumbs: PageBreadcrumb[] = useMemo(() => {
    if (user) {
      return [
        {
          title: PAGE_TITLE,
        },
        {
          title: user.email ?? '',
        },
      ];
    }

    return [];
  }, [user]);

  useEffect(() => {
    setPageTitle?.(PAGE_TITLE);
    setPageBreadcrumbs?.(breadcrumbs);

    return () => {
      setPageTitle?.(null);
      setPageBreadcrumbs?.(null);
    };
  }, [breadcrumbs, setPageTitle, setPageBreadcrumbs]);

  // Settings must mirror the login rule: only SAML-only tenants can opt out of app-managed 2FA.
  useEffect(() => {
    let isMounted = true;

    const loadTwoFactorPolicy = async () => {
      try {
        const [firmListResponse, discoveryResponse] = await Promise.all([
          firmsAction.getFirmList(),
          user?.email ? SsoService.apiSsoDiscoverCreate({ email: user.email }) : Promise.resolve(null),
        ]);

        if (!isMounted) {
          return;
        }

        const organizationRequiresTwoFactor = Boolean(firmListResponse?.requires_2fa);
        const usesSamlOnlyFlow = discoveryResponse?.post_discovery_flow === TenantEmailDiscoveryResponse.post_discovery_flow.SAML;

        setIsTwoFactorMandatory(organizationRequiresTwoFactor || !usesSamlOnlyFlow);
      } catch {
        if (!isMounted) {
          return;
        }

        setIsTwoFactorMandatory(true);
      }
    };

    if (user?.id) {
      loadTwoFactorPolicy();
    }

    return () => {
      isMounted = false;
    };
  }, [user?.email, user?.id]);

  useEffect(() => {
    const getUserTOTP = async () => {
      try {
        const response = await TotpService.apiTotpGetList();

        if (response) {
          setUser2faStatus(response);
        }
      } catch (error: unknown) {
        Logger.error('Error getting TOTP', error);
        throw new Error(error as string);
      }
    };

    if (user) {
      getUserTOTP();
    }
  }, [user]);

  useEffect(() => {
    const getUserBackupCodes = async () => {
      try {
        const response = await TotpService.apiTotpStaticGetList();

        if (response) {
          setBackupCodes(response.tokens);
          if (response.tokens.length === 0) {
            await generateNewBackupCodes();
          }
        }
      } catch (error: unknown) {
        // If we receive a 404 error - "No TOTP device found", we need to generate new backup codes
        if ((error as { status?: number })?.status === 404) {
          await generateNewBackupCodes();
        }
      }
    };

    if (user2faStatus.user_has_totp_device) {
      getUserBackupCodes();
    }
  }, [generateNewBackupCodes, user2faStatus.user_has_totp_device]);

  const setOtpDeviceData = useCallback((updateDeviceData: (prevData: DeviceData) => DeviceData) => {
    setUser2faStatus(prevStatus => ({
      ...prevStatus,
      ...updateDeviceData(prevStatus),
    }));
  }, []);

  const handleOtpVerified = useCallback((verified: boolean) => {
    if (verified) {
      setShowOTP(false);
    }
  }, []);

  return (
    <>
      <Accordion>
        <AccordionSummary className="accordionSummary" expandIcon={<ExpandMoreIcon className="icon" />}>
          <StyledHeading>Activate/Deactivate {PAGE_TITLE}</StyledHeading>
        </AccordionSummary>
        <AccordionDetails>
          {isTwoFactorMandatory ? (
            <Typography color="textSecondary" sx={{ pb: 1.5 }} variant="body2">
              {TWO_FA_ORG_MANDATORY_HINT}
            </Typography>
          ) : null}
          <StyledFormGroup row>
            <FormControlLabel
              control={
                <Switch
                  checked={user2faStatus.confirmed && backupCodes.length > 0}
                  onChange={handle2faChange}
                  name="user2fa"
                  color="primary"
                  disabled={isTwoFactorMandatory && user2faStatus.confirmed}
                  disableRipple
                />
              }
              label={
                <Typography variant="body2" color="textPrimary">
                  Enable {PAGE_TITLE}
                </Typography>
              }
              labelPlacement="start"
            />
          </StyledFormGroup>
        </AccordionDetails>
      </Accordion>
      {displayReauthenticate
        ? user2faStatus?.confirmed && (
          <StyledMessageContainer alignItems="center" container>
            <Grid>
              <StyledMessage>{REAUTHENTICATION_MESSAGE}</StyledMessage>
            </Grid>

            <Grid>
              <SignOutButton />
            </Grid>
          </StyledMessageContainer>
        )
        : user2faStatus?.confirmed
          && backupCodes.length > 0 && (
          <Accordion>
            <AccordionSummary className="accordionSummary" expandIcon={<ExpandMoreIcon className="icon" />}>
              <StyledHeading>{PAGE_TITLE} Backup Codes</StyledHeading>
            </AccordionSummary>

            <AccordionDetails>
              <BackupCodes
                backupCodes={backupCodes}
                disabledGenerate={!user2faStatus?.confirmed}
                onGenerateNewCodes={generateNewBackupCodes}
                isGeneratingBackupCodes={isGeneratingBackupCodes}
                username={user?.email}
              />
            </AccordionDetails>
          </Accordion>
        )}
      <Dialog open={showOTP} onClose={handleDialogClose}>
        <Otp
          showMandatoryHint={isTwoFactorMandatory}
          userHas2FA={false}
          showActionButtons={false}
          setDeviceData={setOtpDeviceData}
          setDisplayReauthenticate={setDisplayReauthenticate}
          verifyOtp={handleOtpVerified}
        />
      </Dialog>
    </>
  );
};

export default User2FA;
